import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import * as XLSX from 'xlsx';
import Layout from '../../components/Layout';

const AttendanceAnalytics = ({ onNavigate, userInfo = {} }) => {
    // Mobile & Sidebar State
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [hoveredItem, setHoveredItem] = useState(null);

    // Data State
    const [subjects, setSubjects] = useState([]);
    const [selectedSubject, setSelectedSubject] = useState('');
    const [analyticsData, setAnalyticsData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [subjectsLoading, setSubjectsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Date State
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    // Check mobile on resize
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 1024);
            if (window.innerWidth > 1024) setIsSidebarOpen(false);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // API Call Helper
    const apiCall = async (endpoint) => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (!token) throw new Error('Authentication token not found');

        const response = await fetch(`${API_LINK}/api/admin${endpoint}`, {
            headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.json();
    };

    // Fetch Subjects
    useEffect(() => {
        const fetchSubjects = async () => {
            try {
                setSubjectsLoading(true);
                const result = await apiCall('/attendance/subjects');
                if (result.success) {
                    setSubjects(result.data);
                }
            } catch (err) {
                console.error('Error fetching subjects:', err);
                setError('Failed to load subjects');
            } finally {
                setSubjectsLoading(false);
            }
        };
        fetchSubjects();
    }, []);

    // Fetch Analytics when Subject Selected
    useEffect(() => {
        if (!selectedSubject) {
            setAnalyticsData(null);
            return;
        }

        const fetchAnalytics = async () => {
            try {
                setLoading(true);
                setError(null);

                let query = `/attendance/analytics?subject=${encodeURIComponent(selectedSubject)}`;
                if (fromDate) query += `&fromDate=${fromDate}`;
                if (toDate) query += `&toDate=${toDate}`;

                const result = await apiCall(query);

                if (result.success) {
                    setAnalyticsData(result.data);
                } else {
                    setError(result.message || 'Failed to fetch analytics');
                }
            } catch (err) {
                console.error('Error fetching analytics:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchAnalytics();
    }, [selectedSubject, fromDate, toDate]);

    // Handle Download
    const handleDownload = (categoryKey, categoryName) => {
        if (!analyticsData || !analyticsData.categories[categoryKey]) return;

        const students = analyticsData.categories[categoryKey].students;
        const subjectName = analyticsData.subject;

        // Prepare data for Excel
        const dataToExport = students.map(student => ({
            'Student ID': student.student_id,
            'Name': student.student_name,
            'Department': student.department,
            'Total Classes': student.total_classes,
            'Classes Attended': student.present_count,
            'Attendance %': `${student.percentage}%`,
            'Status': 'Active', // Assuming active
            'From Date': fromDate || 'Start',
            'To Date': toDate || 'Present'
        }));

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance Report");

        const fileName = `${subjectName}_${categoryName}_Attendance_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);
    };

    // Navigation Handler
    const handleNavigation = (page) => {
        if (onNavigate) {
            if (page === 'Logout') {
                sessionStorage.clear();
                localStorage.clear();
                onNavigate('home');
            } else {
                // Map standard labels to route names if needed, or consistent naming
                const routeMap = {
                    'Dashboard': 'admin-dashboard',
                    'Student Management': 'admin-students',
                    'Teacher Management': 'admin-teachers',
                    'Attendance Records': 'admin-attendance',
                    'Teacher Attendance': 'admin-teacher-attendance',
                    'Attendance Analytics': 'admin-attendance-analytics', // Self
                };
                onNavigate(routeMap[page] || page);
            }
        }
    };

    const getUserInitials = (name) => {
        if (!name) return 'AD';
        const parts = name.split(' ');
        if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        return name.substring(0, 2).toUpperCase();
    };

    return (
        <Layout role="admin" onNavigate={onNavigate} activePage="admin-attendance-analytics" userInfo={userInfo}>
            {/* Subject Selection */}
                <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', marginBottom: '2rem' }}>
                    <h3 style={{ margin: '0 0 1rem 0' }}>Select Subject</h3>
                    <select
                        value={selectedSubject}
                        onChange={(e) => setSelectedSubject(e.target.value)}
                        style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', border: '1px solid #ddd', fontSize: '1rem' }}
                        disabled={subjectsLoading}
                    >
                        <option value="">-- Select a Subject --</option>
                        {subjects.map(sub => (
                            <option key={sub} value={sub}>{sub}</option>
                        ))}
                    </select>
                </div>

                {loading && <div style={{ textAlign: 'center', padding: '2rem' }}>Loading analytics...</div>}

                {error && <div style={{ color: 'red', textAlign: 'center', padding: '1rem' }}>{error}</div>}

                {analyticsData && !loading && (
                    <div>
                        <div style={{ marginBottom: '1.5rem' }}>
                            <h2 style={{ margin: 0 }}>Analytics for {analyticsData.subject}</h2>
                            <p style={{ color: '#666' }}>Total Students: {analyticsData.totalStudents} | Department: {analyticsData.department}</p>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '1.5rem' }}>

                            {/* < 50% Card */}
                            <div style={{ background: '#fee2e2', borderRadius: '12px', padding: '1.5rem', border: '1px solid #fca5a5' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                    <h3 style={{ margin: 0, color: '#b91c1c' }}>Below 50%</h3>
                                    <span style={{ background: 'white', padding: '0.2rem 0.6rem', borderRadius: '20px', fontWeight: 'bold', color: '#b91c1c' }}>
                                        {analyticsData.categories.below50.count}
                                    </span>
                                </div>
                                <p style={{ marginBottom: '1.5rem', fontSize: '0.9rem', color: '#7f1d1d' }}>
                                    Critical attendance levels. Immediate action required.
                                </p>
                                <button
                                    onClick={() => handleDownload('below50', 'Below_50')}
                                    style={{ width: '100%', padding: '0.8rem', background: '#dc2626', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                                >
                                    Download List 📥
                                </button>
                            </div>

                            {/* 50-75% Card */}
                            <div style={{ background: '#ffedd5', borderRadius: '12px', padding: '1.5rem', border: '1px solid #fdba74' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                    <h3 style={{ margin: 0, color: '#c2410c' }}>50% - 75%</h3>
                                    <span style={{ background: 'white', padding: '0.2rem 0.6rem', borderRadius: '20px', fontWeight: 'bold', color: '#c2410c' }}>
                                        {analyticsData.categories.below75.count}
                                    </span>
                                </div>
                                <p style={{ marginBottom: '1.5rem', fontSize: '0.9rem', color: '#7c2d12' }}>
                                    Warning zone. Monitor these students closely.
                                </p>
                                <button
                                    onClick={() => handleDownload('below75', 'Between_50_75')}
                                    style={{ width: '100%', padding: '0.8rem', background: '#ea580c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                                >
                                    Download List 📥
                                </button>
                            </div>

                            {/* > 90% Card */}
                            <div style={{ background: '#dcfce7', borderRadius: '12px', padding: '1.5rem', border: '1px solid #86efac' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                    <h3 style={{ margin: 0, color: '#15803d' }}>Above 90%</h3>
                                    <span style={{ background: 'white', padding: '0.2rem 0.6rem', borderRadius: '20px', fontWeight: 'bold', color: '#15803d' }}>
                                        {analyticsData.categories.above90.count}
                                    </span>
                                </div>
                                <p style={{ marginBottom: '1.5rem', fontSize: '0.9rem', color: '#14532d' }}>
                                    Excellent attendance.
                                </p>
                                <button
                                    onClick={() => handleDownload('above90', 'Above_90')}
                                    style={{ width: '100%', padding: '0.8rem', background: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}
                                >
                                    Download List 📥
                                </button>
                            </div>
                        </div>

                        {/* Detailed List Preview (Optional - showing first few) */}
                        <div style={{ marginTop: '2rem', background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)' }}>
                            <h3>Quick Preview</h3>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
                                    <thead>
                                        <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                                            <th style={{ padding: '1rem', textAlign: 'left' }}>Student Name</th>
                                            <th style={{ padding: '1rem', textAlign: 'left' }}>ID</th>
                                            <th style={{ padding: '1rem', textAlign: 'center' }}>Total Classes</th>
                                            <th style={{ padding: '1rem', textAlign: 'center' }}>Attended</th>
                                            <th style={{ padding: '1rem', textAlign: 'center' }}>%</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {Boolean(analyticsData.categories.below50.students.length) && (
                                            analyticsData.categories.below50.students.slice(0, 3).map(st => (
                                                <tr key={st.student_id} style={{ borderBottom: '1px solid #eee' }}>
                                                    <td style={{ padding: '1rem' }}>{st.student_name} <span style={{ fontSize: '0.8rem', color: 'red' }}>(Low)</span></td>
                                                    <td style={{ padding: '1rem' }}>{st.student_id}</td>
                                                    <td style={{ padding: '1rem', textAlign: 'center' }}>{st.total_classes}</td>
                                                    <td style={{ padding: '1rem', textAlign: 'center' }}>{st.present_count}</td>
                                                    <td style={{ padding: '1rem', textAlign: 'center', fontWeight: 'bold', color: '#dc2626' }}>{st.percentage}%</td>
                                                </tr>
                                            ))
                                        )}
                                        {/* Show message if no data to preview */}
                                        {!analyticsData.categories.below50.students.length && (
                                            <tr><td colSpan="5" style={{ padding: '1rem', textAlign: 'center', color: '#888' }}>Select a category to download full report.</td></tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
        </Layout>
    );
};

export default AttendanceAnalytics;
